// Feature: net-worth-dashboard
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp } from "lucide-react";

export type TimePeriod = "1m" | "3m" | "6m" | "1y" | "all";

export interface NetWorthSnapshot {
  id: string;
  user_id: string;
  snapshot_date: string; // ISO date string YYYY-MM-DD
  total_assets: number;
  total_liabilities: number;
  net_worth: number;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface NetWorthTrendChartProps {
  snapshots: NetWorthSnapshot[];
  displayCurrency: string;
  timePeriod: TimePeriod;
  onTimePeriodChange: (period: TimePeriod) => void;
  isLoading: boolean;
}

const TIME_PERIOD_LABELS: Record<TimePeriod, string> = {
  "1m": "1M",
  "3m": "3M",
  "6m": "6M",
  "1y": "1Y",
  all: "All",
};

const SHORT_PERIODS: TimePeriod[] = ["1m", "3m"];

/**
 * Format Y-axis tick value as currency.
 * Exported for property-based testing.
 */
export function formatYAxis(value: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format X-axis date based on the selected time period.
 * Short periods (≤3m) → day/month (e.g. "15 Jan")
 * Long periods (>3m)  → month/year (e.g. "Jan 2024")
 * Exported for property-based testing.
 */
export function formatXAxis(dateStr: string, timePeriod: TimePeriod): string {
  const date = new Date(dateStr);
  if (SHORT_PERIODS.includes(timePeriod)) {
    // day/month format
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  }
  // month/year format
  return date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}

/** Custom tooltip shown on hover */
function CustomTooltip({
  active,
  payload,
  displayCurrency,
}: {
  active?: boolean;
  payload?: any[];
  displayCurrency: string;
}) {
  if (!active || !payload || !payload.length) return null;

  const point = payload[0].payload as NetWorthSnapshot;
  const date = new Date(point.snapshot_date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: displayCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(point.net_worth);

  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
      <p className="text-sm text-muted-foreground mb-1">{date}</p>
      <p className="text-base font-semibold">{formatted}</p>
    </div>
  );
}

/** Time period selector tabs */
function TimePeriodSelector({
  timePeriod,
  onTimePeriodChange,
}: {
  timePeriod: TimePeriod;
  onTimePeriodChange: (period: TimePeriod) => void;
}) {
  return (
    <div
      className="flex gap-1"
      role="group"
      aria-label="Select time period"
    >
      {(Object.keys(TIME_PERIOD_LABELS) as TimePeriod[]).map((period) => (
        <button
          key={period}
          onClick={() => onTimePeriodChange(period)}
          aria-pressed={timePeriod === period}
          className={`px-2 py-1 text-xs rounded transition-colors ${
            timePeriod === period
              ? "bg-primary text-primary-foreground font-semibold"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        >
          {TIME_PERIOD_LABELS[period]}
        </button>
      ))}
    </div>
  );
}

export function NetWorthTrendChart({
  snapshots,
  displayCurrency,
  timePeriod,
  onTimePeriodChange,
  isLoading,
}: NetWorthTrendChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" aria-hidden="true" />
              Net Worth Trend
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div
            className="space-y-3"
            aria-label="Loading net worth trend chart"
          >
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-[260px] w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" aria-hidden="true" />
            Net Worth Trend
          </CardTitle>
          <TimePeriodSelector
            timePeriod={timePeriod}
            onTimePeriodChange={onTimePeriodChange}
          />
        </div>
      </CardHeader>
      <CardContent>
        {snapshots.length === 0 ? (
          <div
            className="h-[260px] flex items-center justify-center"
            aria-label="No net worth data available"
          >
            <p className="text-muted-foreground text-sm">
              No data available for the selected period
            </p>
          </div>
        ) : (
          <div
            role="img"
            aria-label={`Net worth trend chart showing ${snapshots.length} data points in ${displayCurrency}`}
          >
            <ResponsiveContainer width="100%" height={260}>
              <LineChart
                data={snapshots}
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="snapshot_date"
                  tickFormatter={(value) => formatXAxis(value, timePeriod)}
                  className="text-xs"
                />
                <YAxis
                  tickFormatter={(value) => formatYAxis(value, displayCurrency)}
                  className="text-xs"
                  width={80}
                />
                <Tooltip
                  content={
                    <CustomTooltip displayCurrency={displayCurrency} />
                  }
                />
                <Line
                  type="monotone"
                  dataKey="net_worth"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={{ fill: "hsl(var(--primary))", r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
